import { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import Card from '@mui/material/Card';
import TableRow from '@mui/material/TableRow';
import { Box, Button, Container, IconButton, Stack } from '@mui/material';
import Page from '../../components/Page';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  reset,
  setCurrentPage,
  setLimit,
  setSkip,
  setStatus,
} from '../../store/registrationRejectionReasons/registrationRejectionReasonsSlice';
import {
  createRegistrationRejectionReason,
  findRegistrationRejectionReasons,
  patchRegistrationRejectionReason,
  removeRegistrationRejectionReason,
} from '../../store/registrationRejectionReasons/api';
import Loader from '../../components/Loader';
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
import { useFormik } from 'formik';
import useToggle from '../../hooks/useToggle';
import Scrollbar from '../../components/Scrollbar';
import Searchbar from '../../components/Searchbar';
import { omit } from 'lodash';
import FormModal from './FormModal';
import { RegistrationRejectionReason } from '../../store/registrationRejectionReasons/api/interface';
import registrationRejectionReasonSchema from '../../validation/registrationRejectionReasonSchema';

const Index: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, status, skip, isLoading } =
    useAppSelector((state) => state.registrationRejectionReasons);
  const [search, setSearch] = useState('');
  const { toggle, onOpen, onClose } = useToggle();

  useEffect(() => {
    if (status === 'idle') {
      dispatch(
        findRegistrationRejectionReasons({
          limit,
          skip,
          ...(search && { search }),
        })
      );
    }
  }, [dispatch, limit, skip, status, search]);

  useEffect(() => {
    dispatch(reset());
  }, [search, dispatch]);

  const {
    handleSubmit,
    values,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
    setValues,
  } = useFormik({
    initialValues: {
      id: '',
      en: '',
      ar: '',
      sortOrder: 0,
      activeState: true,
    },
    validationSchema: registrationRejectionReasonSchema,
    onSubmit: async (formValues) => {
      try {
        if (formValues.id) {
          await dispatch(
            patchRegistrationRejectionReason({
              id: formValues.id,
              en: formValues.en,
              ar: formValues.ar,
              sortOrder: Number(formValues.sortOrder),
              activeState: formValues.activeState,
            })
          ).unwrap();
        } else {
          const reqBody = omit(formValues, ['id']);
          await dispatch(
            createRegistrationRejectionReason({
              ...reqBody,
              sortOrder: Number(reqBody.sortOrder),
            })
          ).unwrap();
        }
        onClose();
        resetForm();
        dispatch(setStatus({ status: 'idle' }));
      } catch (error: any) {}
    },
  });

  const handleUpdate = (value: RegistrationRejectionReason): void => {
    onOpen();
    setValues({
      id: value.id,
      en: value.en,
      ar: value.ar,
      sortOrder: value.sortOrder ?? 0,
      activeState: value.activeState,
    });
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this rejection reason?')) return;
    try {
      await dispatch(removeRegistrationRejectionReason(id)).unwrap();
      dispatch(setStatus({ status: 'idle' }));
    } catch (e) {}
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setStatus({ status: 'idle' }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  return (
    <Page title='Rejection Reasons'>
      <Container maxWidth='xl'>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='flex-end'
          spacing={{ xs: 0.5, sm: 1.5 }}
          my={1}
        >
          <Button
            variant='contained'
            startIcon={<Iconify icon={'eva:plus-fill'} />}
            onClick={() => {
              resetForm();
              onOpen();
            }}
          >
            New Reason
          </Button>
        </Stack>

        <Card>
          <Searchbar
            handleSearch={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearch(e.target.value)
            }
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 1000, position: 'relative' }}>
              <Table size='medium'>
                <TableHead>
                  <TableRow>
                    <TableCell align='left'>English</TableCell>
                    <TableCell align='right'>Arabic</TableCell>
                    <TableCell align='center'>Order</TableCell>
                    <TableCell align='center'>Status</TableCell>
                    <TableCell align='center'>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} align='center'>
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : data?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align='center'>
                        No record found
                      </TableCell>
                    </TableRow>
                  ) : (
                    data?.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell align='left'>{item.en}</TableCell>
                        <TableCell align='right' dir='rtl'>
                          {item.ar}
                        </TableCell>
                        <TableCell align='center'>{item.sortOrder}</TableCell>
                        <TableCell align='center'>
                          <Label
                            variant='ghost'
                            color={item.activeState ? 'success' : 'error'}
                          >
                            {item.activeState ? 'Active' : 'Inactive'}
                          </Label>
                        </TableCell>
                        <TableCell align='center'>
                          <IconButton
                            aria-label='edit'
                            onClick={() => handleUpdate(item)}
                          >
                            <Iconify icon={'eva:edit-fill'} />
                          </IconButton>
                          <IconButton
                            aria-label='delete'
                            onClick={() => handleDelete(item.id)}
                          >
                            <Iconify icon={'eva:trash-2-outline'} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component='div'
              count={totalRecords}
              rowsPerPage={limit}
              page={Math.min(
                Math.max(currentPage, 0),
                Math.max(0, Math.ceil(totalRecords / limit) - 1)
              )}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        </Card>

        <FormModal
          touched={touched}
          errors={errors}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          onClose={onClose}
          resetForm={resetForm}
          setFieldValue={setFieldValue}
          toggle={toggle}
          values={values}
        />
      </Container>
    </Page>
  );
};

export default Index;
